import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { supabase } from '../lib/supabase.js';

/**
 * レート確定を受け取って、レートを表示している画面を取り直させる。
 *
 * コンテストを終了するとサーバー側でレートが確定する (SPEC 87)。
 * これまでは、それを見ている人の画面は次にページを開くまで古いままだった。
 * rating_history への insert を Realtime で受け、該当するクエリを無効化する。
 *
 * ポーリングにしないのは、レート確定が 1 日に数回あるかどうかの出来事で、
 * そのために全利用者が数秒おきに API を叩くのは割に合わないため。
 *
 * 接続は Supabase へ直接張る。API（トンネル越しの自宅サーバー）を経由しない。
 */

/**
 * 1 回のレート確定で、参加者の数だけ insert が飛んでくる。
 * 1 件ごとに取り直すと、数千人規模で同じリクエストを大量に撃つことになる。
 * 最初の 1 件から少し待って、まとめて 1 回だけ無効化する。
 */
const COALESCE_MS = 1500;

export function useRatingRealtime(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const invalidate = (): void => {
      timer = null;
      // レートが出ている場所をまとめて取り直す。
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['ranking'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      // 順位表とコンテスト詳細にもレートが出る。
      void queryClient.invalidateQueries({ queryKey: ['contests'] });
    };

    const channel = supabase
      .channel('rating-updates')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'rating_history' },
        () => {
          if (timer === null) {
            timer = setTimeout(invalidate, COALESCE_MS);
          }
        },
      )
      .subscribe();

    return () => {
      if (timer !== null) {
        clearTimeout(timer);
      }
      void supabase.removeChannel(channel);
    };
  }, [queryClient]);
}
